import React, { useEffect, useMemo, useState } from "react";
import apiService from "../../services/api";
import styles from "./CourseVisibilityManager.module.css";

/**
 * Props expected from CourseManagement.js
 * - courseId
 * - courseName
 * - onClose()
 */
const CourseVisibilityManager = ({ courseId, courseName, onClose }) => {
  const [settings, setSettings] = useState(null);
  const [activeSection, setActiveSection] = useState("Student");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  // Optional audit log modal (kept from your existing structure)
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogData, setAuditLogData] = useState([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);

  const visibilityCategories = useMemo(
    () => [
      {
        name: "Student",
        description: "Control what students can see (modules, content, tools).",
        items: [
          { key: "show_quizzes", label: "Quizzes" },
          { key: "show_attendance", label: "Attendance" },
          { key: "show_assignments", label: "Assignments" },
          { key: "show_announcements", label: "Announcements" },
          { key: "show_recordings", label: "Recordings" },
          { key: "show_syllabus", label: "Syllabus" },
          { key: "show_slides", label: "Slides" },
          { key: "show_readings", label: "Readings" },
          { key: "show_whatsapp_group", label: "WhatsApp Group" },
          { key: "show_virtual_session", label: "Virtual Session" },
          { key: "show_electives", label: "Electives" },
        ],
      },
      {
        name: "Registration",
        description: "Control course registration access.",
        items: [{ key: "allow_registration", label: "Allow Registration" }],
      },
    ],
    []
  );

  const activeCategory = visibilityCategories.find((c) => c.name === activeSection);

  const fetchJson = async (endpoint, options = {}) => {
    const method = (options.method || "GET").toUpperCase();
    const headers = {
      ...(options.headers || {}),
    };

    const token = localStorage.getItem("faithinsight_token");
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    if (["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
      const csrfToken = await apiService.fetchCSRFToken();
      if (!csrfToken) {
        throw new Error(
          "Failed to obtain CSRF token. Please refresh the page and try again."
        );
      }
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(`${apiService.baseURL}${endpoint}`, {
      credentials: "include",
      ...options,
      method,
      headers,
    });

    const contentType = response.headers.get("content-type") || "";

    if (!response.ok) {
      const bodyText = await response.text();
      console.error("Visibility fetch failed", {
        url: response.url,
        status: response.status,
        contentType,
      });

      if (response.status === 401 || response.status === 403) {
        throw new Error("You don’t have access to this resource. Please refresh and sign in again.");
      }

      throw new Error(
        `Request failed with status ${response.status}. Body: ${bodyText.slice(0, 200)}`
      );
    }

    if (!contentType.includes("application/json")) {
      const bodyText = await response.text();
      console.error("Visibility fetch expected JSON", {
        url: response.url,
        status: response.status,
        contentType,
      });
      throw new Error(
        `Expected JSON but got ${contentType || "unknown"}. Body: ${bodyText.slice(0, 200)}`
      );
    }

    return response.json();
  };

  // ---------- Modal UX (ESC close + scroll lock) ----------
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // ---------- Data ----------
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        setError("");

        const data = await fetchJson(`/courses/${courseId}/visibility`);
        setSettings(data?.visibility || data);
      } catch (err) {
        setError(err?.message || "Failed to load settings");
      } finally {
        setIsLoading(false);
      }
    };

    if (courseId) loadSettings();
  }, [courseId]);

  const handleToggle = (itemKey) => {
    setSettings((prev) => ({
      ...prev,
      [itemKey]: !prev?.[itemKey],
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError("");

      await fetchJson(`/courses/${courseId}/visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
    } catch (err) {
      setError(err?.message || "Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const loadAuditLog = async () => {
    try {
      setIsAuditLoading(true);
      const data = await fetchJson(`/courses/${courseId}/visibility/audit`);
      setAuditLogData(Array.isArray(data?.audit_log) ? data.audit_log : []);
    } catch (err) {
      // keep audit errors non-blocking
      console.error(err);
      setAuditLogData([]);
    } finally {
      setIsAuditLoading(false);
    }
  };

  const openAudit = async () => {
    setShowAuditLog(true);
    await loadAuditLog();
  };

  // ---------- Render ----------
  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.header}>
          <div className={styles.headerText}>
            <div className={styles.title}>Course Access Settings</div>
            <div className={styles.subtitle}>
              <span className={styles.courseName}>{courseName}</span>
              <span className={styles.dot}>•</span>
              <span className={styles.courseId}>Course ID: {courseId}</span>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button type="button" className={styles.ghostBtn} onClick={openAudit}>
              View Audit Log
            </button>
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className={styles.content}>
          <aside className={styles.sidebar}>
            <div className={styles.sidebarLabel}>Sections</div>
            <div className={styles.nav}>
              {visibilityCategories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  className={`${styles.navItem} ${activeSection === cat.name ? styles.navItemActive : ""}`}
                  onClick={() => setActiveSection(cat.name)}
                >
                  <div className={styles.navTitle}>{cat.name}</div>
                  <div className={styles.navDesc}>{cat.description}</div>
                </button>
              ))}
            </div>
          </aside>

          <main className={styles.main}>
            <div className={styles.sectionHeader}>
              <div>
                <div className={styles.sectionTitle}>{activeSection} Visibility</div>
                <div className={styles.sectionHint}>
                  Toggle items to control visibility. Changes apply immediately after saving.
                </div>
              </div>
            </div>

            {error ? <div className={styles.errorBanner}>{error}</div> : null}

            <div className={styles.panel}>
              {isLoading ? (
                <div className={styles.stateText}>Loading settings…</div>
              ) : !settings || !activeCategory ? (
                <div className={styles.stateText}>No settings available.</div>
              ) : (
                <div className={styles.toggleGrid}>
                  {activeCategory.items.map((item) => (
                    <div key={item.key} className={styles.toggleRow}>
                      <div className={styles.toggleMeta}>
                        <div className={styles.toggleName}>{item.label}</div>
                        <div className={styles.toggleSub}>
                          {settings?.[item.key] ? "Visible" : "Hidden"}
                        </div>
                      </div>

                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          checked={!!settings?.[item.key]}
                          onChange={() => handleToggle(item.key)}
                        />
                        <span className={styles.slider} />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                Close
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handleSave}
                disabled={isSaving || isLoading || !settings}
              >
                {isSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </main>
        </div>

        {/* Audit Log Modal (simple, nested) */}
        {showAuditLog && (
          <div className={styles.auditOverlay} onMouseDown={() => setShowAuditLog(false)}>
            <div className={styles.auditModal} onMouseDown={(e) => e.stopPropagation()}>
              <div className={styles.auditHeader}>
                <div className={styles.auditTitle}>Audit Log</div>
                <button className={styles.iconBtn} onClick={() => setShowAuditLog(false)} aria-label="Close audit log">
                  ✕
                </button>
              </div>

              <div className={styles.auditBody}>
                {isAuditLoading ? (
                  <div className={styles.stateText}>Loading audit log…</div>
                ) : auditLogData.length === 0 ? (
                  <div className={styles.stateText}>No audit entries yet.</div>
                ) : (
                  <div className={styles.auditList}>
                    {auditLogData.map((item, idx) => (
                      <div className={styles.auditRow} key={idx}>
                        <div className={styles.auditLine}>
                          <span className={styles.auditStrong}>{item?.actor || "System"}</span>{" "}
                          changed <span className={styles.auditStrong}>{item?.field}</span>
                        </div>
                        <div className={styles.auditMeta}>
                          {item?.timestamp || ""} {item?.notes ? `• ${item.notes}` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.auditFooter}>
                <button className={styles.secondaryBtn} onClick={() => setShowAuditLog(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseVisibilityManager;
